import { saveAs } from 'file-saver';
import { Subscription as rxjsSubscription } from 'rxjs';
import { Component, Input, OnInit, ViewChild, Output, EventEmitter, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ModalComponent, PopUpService } from '../../../core';
import { QuestionBase, DynamicFormComponent } from '../../../core/components/forms';
import { Client, Subscription } from 'core/models';
import { SubscriptionsService } from 'core/services/subscriptions.service';
import { SubscriptionQuestionService } from '../../questions-services/subscription-questions.service';

@Component({
  selector: 'lms-client-subscription-editor',
  templateUrl: './client-subscription-editor.component.html',
  providers: [SubscriptionQuestionService]
})
export class ClientSubscriptionEditorComponent implements OnInit, OnDestroy {
  @Input() client: Client;
  @Input() subscription: Subscription;
  @Input() subscriptionRenewal: Subscription;
  @Output() onSave = new EventEmitter<Subscription>();
  @ViewChild('modalSubscriptionForm', { static: false }) public modalComponent: ModalComponent;
  @ViewChild('dynamicForm', { static: false }) public dynamicForm: DynamicFormComponent;
  @Output() public formErrorEmit: EventEmitter<object> = new EventEmitter<object>();

  public questions: QuestionBase<any>[] = [];
  public hasSubscription: Boolean = false;
  public isSubscriptionRenewal: boolean;

  private downloadSeatsReportSubscription: rxjsSubscription = new rxjsSubscription();

  constructor(
    private subscriptionsService: SubscriptionsService,
    private subscriptionQuestionsService: SubscriptionQuestionService,
    private popupService: PopUpService,
    private translate: TranslateService
  ) {}

  ngOnInit() {}

  public ngOnChanges(): void {
    this.hasSubscription = !!this.subscription;
  }

  public ngOnDestroy(): void {
    this.downloadSeatsReportSubscription.unsubscribe();
  }

  private setQuestions(): void {
    this.questions = this.isSubscriptionRenewal
      ? this.subscriptionQuestionsService.getSubscriptionRenewalQuestions(
          this.subscription,
          this.subscriptionRenewal,
          this.client
        )
      : this.subscriptionQuestionsService.getSubscriptionQuestions(this.subscription, this.client);
  }

  public openSubscriptionEditor(): void {
    this.openModal();
  }

  public openRenewalEditor(): void {
    this.openModal(true);
  }

  public openModal(renewal = false): void {
    this.isSubscriptionRenewal = renewal;
    this.setQuestions();
    this.modalComponent.open();
  }

  public onCancel(): void {
    this.modalComponent.close();
  }

  public onSubmit(subscriptionData: Subscription): void {
    if (this.isSubscriptionRenewal) {
      this.onRenewSubscription(subscriptionData);
    } else if (this.hasSubscription) {
      this.onUpdate(subscriptionData);
    } else {
      this.onCreate(subscriptionData);
    }
    this.modalComponent.close();
  }

  private onCreate(subscriptionData: Object): void {
    this.subscriptionsService
      .create(subscriptionData)
      .then((subscription) => {
        this.endSubmit(null);
        this.onSave.emit(subscription);
      })
      .catch((err) => {
        this.formErrorEmit.emit(err);
        this.endSubmit(err);
      });
  }

  private onUpdate(updatedData: Object): void {
    this.subscriptionsService
      .update({ id: this.subscription.id, ...updatedData })
      .then((updatedSubscription) => {
        this.endSubmit(null);
        this.onSave.emit(updatedSubscription);
      })
      .catch((err) => {
        this.formErrorEmit.emit(err);
        this.endSubmit(err);
      });
  }

  private onRenewSubscription(updatedData: Object): void {
    this.subscriptionsService
      .renewSubscription(this.subscription.id, updatedData)
      .then((updatedSubscription) => {
        this.endSubmit(null);
        this.onSave.emit(updatedSubscription);
      })
      .catch((err) => {
        this.formErrorEmit.emit(err);
        this.endSubmit(err);
      });
  }

  private endSubmit(value): void {
    this.dynamicForm.submitted(value);
  }

  public deleteSubscription(): void {
    this.popupService
      .confirmDelete({
        title: this.translate.instant('subscriptions.confirmDelete'),
        text: this.translate.instant('subscriptions.deleteMessage')
      })
      .then(() => this.subscriptionsService.delete(this.subscription.id))
      .then(() => this.onSave.emit())
      .catch((err) => {
        if (err !== 'cancel') {
          this.popupService.showError({
            title: this.translate.instant('subscriptions.deleteError'),
            text: err.serverMessage
          });
        }
      });
  }

  public deleteSubscriptionRenewal(): void {
    this.popupService
      .confirmDelete({
        title: this.translate.instant('subscriptions.deleteRenewal'),
        size: 'small'
      })
      .then(() => {
        this.onCancel();
        return this.subscriptionsService.delete(this.subscriptionRenewal.id);
      })
      .then(() => this.onSave.emit())
      .catch((err) => {
        if (err !== 'cancel') {
          this.popupService.showError({
            title: this.translate.instant('subscriptions.deleteError'),
            text: err.serverMessage
          });
        }
      });
  }

  public downloadSeatsReport(): void {
    this.downloadSeatsReportSubscription = this.subscriptionsService
      .downloadSeatsReport(this.client.id)
      .subscribe((res) => {
        const clientName = this.client.name.split(' ').join('');

        saveAs(res, `SeatsReport_${clientName}.csv`);
      });
  }
}
